import { Controller } from '@hotwired/stimulus';
import Dropzone from 'dropzone';
import { Turbo } from '@hotwired/turbo-rails';

// Prevents Dropzone from automatically discovering and attaching to form elements
Dropzone.autoDiscover = false;

export default class extends Controller {
  static values = {
    paramName: { type: String, default: 'content[file]' },
    maxFiles: { type: Number, default: 10 },
    maxFilesize: { type: Number, default: 50 },
    acceptedFiles: { type: String, default: 'image/*' },
    previewsTarget: { type: String, default: null },
  };

  connect() {
    console.log('Dropzone controller connected');

    // Get the form element
    const form = this.element.closest('form');
    const formAction = form ? form.getAttribute('action') : null;

    // Initialize Dropzone
    this.dropzone = new Dropzone(this.element, {
      url: formAction,
      paramName: this.paramNameValue,
      maxFiles: this.maxFilesValue,
      addRemoveLinks: true,
      maxFilesize: this.maxFilesizeValue,
      parallelUploads: 1, // Only one file at a time
      acceptedFiles: this.acceptedFilesValue,
      autoProcessQueue: true,
      uploadMultiple: false,
      createImageThumbnails: true,
      previewsContainer: '.dropzone-preview',
      headers: {
        'X-CSRF-Token': document.querySelector("meta[name='csrf-token']")
          .content,
        Accept: 'text/vnd.turbo-stream.html',
      },
    });

    // Set up event handlers
    this.setupEvents();
  }

  setupEvents() {
    // When a file is added
    this.dropzone.on('addedfile', (file) => {
      // Add hidden form fields to the upload
      const form = this.element.closest('form');
      const inputs = form.querySelectorAll('input[type=hidden]');
      const params = {};

      inputs.forEach((input) => {
        if (input.name) {
          params[input.name] = input.value;
        }
      });

      this.dropzone.options.params = params;

      // Move the preview to the beginning of the container if it's in a grid
      if (
        file.previewElement &&
        file.previewElement.parentNode &&
        file.previewElement.parentNode.classList.contains('grid')
      ) {
        file.previewElement.parentNode.insertBefore(
          file.previewElement,
          file.previewElement.parentNode.firstChild
        );
      }
    });

    // When a file is being sent
    this.dropzone.on('sending', (file, xhr, formData) => {
      // Add authenticity token
      const csrfToken = document.querySelector(
        "meta[name='csrf-token']"
      ).content;
      formData.append('authenticity_token', csrfToken);

      // Set response type to text for Turbo Stream
      xhr.responseType = 'text';
    });

    // When a file is successfully uploaded
    this.dropzone.on('success', (file, response) => {
      try {
        // Process Turbo Stream response
        const turboStream =
          typeof response === 'string' ? response : response.toString();
        Turbo.renderStreamMessage(turboStream);

        // Remove the file preview after a short delay
        setTimeout(() => {
          this.dropzone.removeFile(file);
        }, 1000);
      } catch (error) {
        console.error('Error processing response:', error);
      }
    });

    // When a file upload fails
    this.dropzone.on('error', (file, errorMessage) => {
      console.error('Upload error:', errorMessage);

      // Remove the file preview after a delay
      setTimeout(() => {
        this.dropzone.removeFile(file);
      }, 2000);
    });

    // When all uploads are complete
    this.dropzone.on('queuecomplete', () => {});
  }

  // Clean up when controller is disconnected
  disconnect() {
    if (this.dropzone) {
      this.dropzone.destroy();
    }
  }
}
